import { useMemo, useState } from 'preact/hooks';
import { useBooks, useStore } from '../hooks';
import { defaultFilters, filterBooks, shelvesOf, isMyCopy, type BookView, type Filters, type Scope } from '../lib/model';
import { Cover } from '../components/Cover';
import { Avatar, Empty, Field, Sheet } from '../components/ui';
import { CATEGORIES, DESTINATIONS, DESTINATION_LABELS, READING_LABELS, READING_STATUSES, type Destination, type ReadingStatus } from '../../shared/types';
import { deleteCopies, updateCopies, updateWorks } from '../lib/actions';
import { toast } from '../components/toast';
import { coverRunner } from '../lib/coverRunner';

const READING_BADGE: Record<string, string> = {
  citit: 'bg-emerald-600 text-white', in_curs: 'bg-amber-500 text-white', vreau: 'bg-sky-600 text-white', abandonat: 'bg-ink-500 text-white'
};

export function Library({ scope, onOpen }: { scope: Scope; onOpen: (workId: string) => void }) {
  const s = useStore();
  const books = useBooks();
  const [f, setF] = useState<Filters>(() => {
    const review = sessionStorage.getItem('cc10_cover_review') === '1';
    sessionStorage.removeItem('cc10_cover_review');
    return { ...defaultFilters(), scope, coverReview: review };
  });
  const [shelvesOpen, setShelvesOpen] = useState(false);
  const filters = { ...f, scope };
  const [view, setView] = useState<'grid' | 'list'>(() => (localStorage.getItem('cc10_view') as any) || 'grid');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batch, setBatch] = useState<null | 'shelf' | 'destination' | 'category' | 'covers' | 'delete'>(null);

  const scoped = useMemo(() => filterBooks(books, { ...defaultFilters(), scope }, s.me), [books, scope, s.me]);
  const list = useMemo(() => filterBooks(books, filters, s.me), [books, f, scope, s.me]);
  const shelves = useMemo(() => shelvesOf(scoped), [scoped]);
  const members = [...s.view.members.values()];
  const activeCount = [f.destination, f.category, f.reading, f.owner, f.coverReview].filter(Boolean).length;

  const toggleSel = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selBooks = list.filter(b => selected.has(b.work.id));
  const isAdmin = s.me?.role === 'admin';
  const myCopiesOf = (bs: BookView[]) => bs.flatMap(b => b.copies).filter(c => isAdmin || c.owner_id === s.me?.id);

  const setView2 = (v: 'grid' | 'list') => { setView(v); localStorage.setItem('cc10_view', v); };

  return (
    <div class="pb-28">
      {/* căutare */}
      <div class="sticky top-[56px] z-20 bg-cream-100/95 backdrop-blur px-4 pt-3 pb-2 space-y-2">
        <div class="flex gap-2">
          <div class="relative flex-1">
            <input type="search" value={f.query} onInput={e => setF({ ...f, query: (e.target as HTMLInputElement).value })}
              placeholder="Caută titlu, autor…  @raft  #copii" aria-label="Caută"
              class="field pl-9" />
            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">⌕</span>
          </div>
          <button class={`btn-ghost relative ${activeCount ? 'border-terra-500 text-terra-600' : ''}`} onClick={() => setFiltersOpen(true)} aria-label="Filtre">
            ☰ Filtre{activeCount > 0 && <span class="ml-1 rounded-full bg-terra-500 px-1.5 text-[11px] text-white">{activeCount}</span>}
          </button>
        </div>
        <div class="flex items-center gap-2">
          <button class={f.shelf ? 'chip-on' : 'chip-off'} onClick={() => setShelvesOpen(true)} data-testid="shelf-menu">
            📍 {f.shelf || 'Toate rafturile'} <span class="opacity-70">▾</span>
          </button>
          {f.shelf && <button class="text-xs text-ink-500 underline" onClick={() => setF({ ...f, shelf: null })}>toate</button>}
          {f.coverReview && <button class="chip-on" onClick={() => setF({ ...f, coverReview: false })}>🖼 Coperți de verificat ×</button>}
        </div>
        <div class="flex items-center justify-between text-sm text-ink-500">
          <span data-testid="count">{list.length} {list.length === 1 ? 'carte' : 'cărți'}</span>
          <div class="flex items-center gap-1">
            <button class={`px-2 py-1 rounded-lg ${view === 'grid' ? 'bg-white text-ink-900 shadow-sm' : ''}`} onClick={() => setView2('grid')} aria-label="Grilă">▦</button>
            <button class={`px-2 py-1 rounded-lg ${view === 'list' ? 'bg-white text-ink-900 shadow-sm' : ''}`} onClick={() => setView2('list')} aria-label="Listă">☰</button>
            <button class={`ml-1 px-3 py-1.5 rounded-lg font-semibold border ${selecting ? 'bg-ink-900 text-white border-ink-900' : 'bg-white text-terra-600 border-terra-200'}`}
              onClick={() => { setSelecting(!selecting); setSelected(new Set()); }} data-testid="select-toggle">{selecting ? '✓ Gata' : '☑ Editare în grup'}</button>
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <Empty title={scoped.length === 0 ? (scope === 'mine' ? 'Raftul tău e gol' : 'Biblioteca familiei e goală') : 'Nicio carte nu se potrivește'}>
          {scoped.length === 0 ? 'Apasă + pentru a adăuga prima carte.' : 'Încearcă alt cuvânt sau scoate filtrele.'}
        </Empty>
      ) : view === 'grid' ? (
        <div class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-x-3 gap-y-5 px-4 pt-2">
          {list.map(b => (
            <button key={b.work.id} class="text-left relative group" data-testid="book"
              onClick={() => selecting ? toggleSel(b.work.id) : onOpen(b.work.id)}>
              <div class={`relative transition ${selected.has(b.work.id) ? 'ring-4 ring-terra-500 rounded-lg' : ''}`}>
                <Cover work={b.work} />
                {b.myReading && b.myReading.status !== 'necitit' && (
                  <span class={`absolute top-1.5 left-1.5 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${READING_BADGE[b.myReading.status] || ''}`}>
                    {b.myReading.status === 'citit' ? '✓ Citită' : READING_LABELS[b.myReading.status]}
                  </span>
                )}
                {b.activeLoans.some(l => l.status === 'aprobat') && <span class="absolute top-1.5 right-1.5 rounded-md bg-ink-900/80 px-1.5 py-0.5 text-[10px] font-bold text-white">↗ Împrumutată</span>}
                {selecting && <span class={`absolute bottom-1.5 right-1.5 h-6 w-6 rounded-full border-2 border-white flex items-center justify-center text-xs ${selected.has(b.work.id) ? 'bg-terra-500 text-white' : 'bg-ink-900/30'}`}>{selected.has(b.work.id) ? '✓' : ''}</span>}
              </div>
              <div class="mt-1.5 text-[13px] font-semibold leading-snug line-clamp-2">{b.work.title}</div>
              <div class="text-[11px] text-ink-500 line-clamp-1">{b.work.authors || '—'}</div>
              {scope === 'family' && b.owners.length > 0 && (
                <div class="mt-1 flex -space-x-1.5">{b.owners.slice(0, 3).map(o => <Avatar key={o.id} member={o} size={18} />)}</div>
              )}
            </button>
          ))}
        </div>
      ) : (
        <ul class="px-4 pt-1 divide-y divide-cream-300">
          {list.map(b => (
            <li key={b.work.id}>
              <button class={`w-full flex gap-3 py-2.5 text-left ${selected.has(b.work.id) ? 'bg-terra-50' : ''}`} data-testid="book"
                onClick={() => selecting ? toggleSel(b.work.id) : onOpen(b.work.id)}>
                <Cover work={b.work} size="sm" />
                <div class="min-w-0 flex-1">
                  <div class="font-semibold leading-snug line-clamp-1">{b.work.title}</div>
                  <div class="text-sm text-ink-500 line-clamp-1">{b.work.authors}</div>
                  <div class="mt-1 flex flex-wrap gap-1 text-[11px]">
                    {[...new Set(b.copies.map(c => c.shelf).filter(Boolean))].slice(0, 2).map(sh => <span key={sh} class="rounded bg-cream-200 px-1.5 py-0.5">📍 {sh}</span>)}
                    {b.myReading && b.myReading.status !== 'necitit' && <span class={`rounded px-1.5 py-0.5 ${READING_BADGE[b.myReading.status]}`}>{READING_LABELS[b.myReading.status]}</span>}
                  </div>
                </div>
                {selecting && <span class={`self-center h-6 w-6 rounded-full border-2 flex items-center justify-center text-xs ${selected.has(b.work.id) ? 'bg-terra-500 border-terra-500 text-white' : 'border-cream-300'}`}>{selected.has(b.work.id) ? '✓' : ''}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* bara de acțiuni în masă */}
      {selecting && (
        <div class="fixed inset-x-0 z-50 px-3" style={{ bottom: 'calc(env(safe-area-inset-bottom) + 72px)' }}>
          <div class="mx-auto max-w-3xl rounded-2xl bg-ink-900 text-white shadow-2xl p-2">
            <div class="flex items-center px-1 pb-1">
              <span class="text-sm font-semibold">{selected.size} selectate</span>
              <span class="flex-1" />
              <button class="px-2 py-1 text-xs font-semibold underline" onClick={() => setSelected(selected.size === list.length ? new Set() : new Set(list.map(b => b.work.id)))}>
                {selected.size === list.length ? 'Deselectează tot' : `Selectează tot (${list.length})`}
              </button>
            </div>
            <div class="grid grid-cols-5 gap-1">
              {(['shelf', 'destination', 'category', 'covers', 'delete'] as const).map(a => (
                <button key={a} data-testid={`batch-${a}`} disabled={selected.size === 0} class={`flex flex-col items-center gap-0.5 rounded-xl py-2 text-[11px] font-semibold disabled:opacity-40 ${a === 'delete' ? 'text-red-300' : 'bg-white/10'}`}
                  onClick={() => setBatch(a)}>
                  <span class="text-base leading-none">{a === 'shelf' ? '📍' : a === 'destination' ? '🏷' : a === 'category' ? '📂' : a === 'covers' ? '🖼' : '🗑'}</span>
                  {a === 'shelf' ? 'Raft' : a === 'destination' ? 'Destinație' : a === 'category' ? 'Categorie' : a === 'covers' ? 'Coperți' : 'Șterge'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <ShelvesSheet open={shelvesOpen} onClose={() => setShelvesOpen(false)} shelves={shelves} current={f.shelf}
        books={scoped} isAdmin={isAdmin} meId={s.me?.id}
        onPick={sh => { setF({ ...f, shelf: sh }); setShelvesOpen(false); }}
        onSelectShelf={sh => { setF({ ...f, shelf: sh }); setSelecting(true); setSelected(new Set(scoped.filter(b => b.copies.some(c => (c.shelf || '') === sh)).map(b => b.work.id))); setShelvesOpen(false); }} />
      <FiltersSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} f={f} setF={setF} scope={scope} members={members} />
      <BatchSheet kind={batch} books={selBooks} shelves={shelves.map(x => x.name)} myCopies={myCopiesOf(selBooks)}
        onClose={() => setBatch(null)} onDone={() => { setBatch(null); setSelecting(false); setSelected(new Set()); }} />
    </div>
  );
}

function FiltersSheet({ open, onClose, f, setF, scope, members }: any) {
  const set = (k: keyof Filters, v: any) => setF({ ...f, [k]: f[k] === v ? null : v });
  return (
    <Sheet open={open} onClose={onClose} title="Filtre"
      footer={<><button class="btn-ghost" onClick={() => setF({ ...defaultFilters(), query: f.query })}>Resetează</button><button class="btn-primary" onClick={onClose}>Gata</button></>}>
      <div class="space-y-5">
        <div>
          <div class="label">Statusul meu de lectură</div>
          <div class="flex flex-wrap gap-2">{READING_STATUSES.map((st: ReadingStatus) => <button key={st} class={f.reading === st ? 'chip-on' : 'chip-off'} onClick={() => set('reading', st)}>{READING_LABELS[st]}</button>)}</div>
        </div>
        <div>
          <div class="label">Destinație</div>
          <div class="flex flex-wrap gap-2">{DESTINATIONS.map((d: Destination) => <button key={d} class={f.destination === d ? 'chip-on' : 'chip-off'} onClick={() => set('destination', d)}>{DESTINATION_LABELS[d]}</button>)}</div>
        </div>
        {scope === 'family' && (
          <div>
            <div class="label">Al cui exemplar</div>
            <div class="flex flex-wrap gap-2">{members.map((m: any) => <button key={m.id} class={f.owner === m.id ? 'chip-on' : 'chip-off'} onClick={() => set('owner', m.id)}><Avatar member={m} size={18} /> {m.name}</button>)}</div>
          </div>
        )}
        <div>
          <div class="label">Categorie</div>
          <div class="flex flex-wrap gap-2">{CATEGORIES.map(c => <button key={c} class={f.category === c ? 'chip-on' : 'chip-off'} onClick={() => set('category', c)}>{c}</button>)}</div>
        </div>
        <div>
          <div class="label">Coperți</div>
          <button class={f.coverReview ? 'chip-on' : 'chip-off'} onClick={() => setF({ ...f, coverReview: !f.coverReview })}>🖼 Alese automat (de verificat)</button>
        </div>
        <Field label="Ordonare">
          <select class="field" value={f.sort} onChange={e => setF({ ...f, sort: (e.target as HTMLSelectElement).value })}>
            <option value="added_desc">Adăugate recent</option>
            <option value="title">Titlu (A–Z)</option>
            <option value="author">Autor (A–Z)</option>
            <option value="rating">Notele mele</option>
          </select>
        </Field>
        <p class="text-xs text-ink-500">Sfat: în căutare scrie <b>@living</b> pentru un raft sau <b>#copii</b>, <b>#vinted</b>, <b>#beletristica</b> pentru etichete.</p>
      </div>
    </Sheet>
  );
}

function BatchSheet({ kind, books, shelves, myCopies, onClose, onDone }: { kind: null | 'shelf' | 'destination' | 'category' | 'covers' | 'delete'; books: BookView[]; shelves: string[]; myCopies: any[]; onClose: () => void; onDone: () => void }) {
  const [value, setValue] = useState('');
  const [replace, setReplace] = useState(false);
  const n = books.length;
  if (!kind) return null;
  const withCover = books.filter(b => b.work.cover_url).length;
  const skipped = kind !== 'category' && kind !== 'covers' ? books.flatMap(b => b.copies).length - myCopies.length : 0;

  const apply = async () => {
    if (kind === 'shelf') { if (!value.trim()) return; await updateCopies(myCopies.map(c => c.id), { shelf: value.trim() }); toast(`${myCopies.length} exemplare mutate pe „${value.trim()}”`); }
    if (kind === 'destination') { if (!value) return; await updateCopies(myCopies.map(c => c.id), { destination: value as Destination }); toast('Destinație actualizată'); }
    if (kind === 'category') { if (!value) return; await updateWorks(books.map(b => b.work.id), { category: value }); toast('Categorie actualizată'); }
    if (kind === 'delete') { await deleteCopies(myCopies.map(c => c.id)); toast(`${myCopies.length} exemplare șterse`); }
    if (kind === 'covers') {
      if (coverRunner.state.running) { toast('O căutare de coperți rulează deja', 'error'); return; }
      const works = books.map(b => b.work);
      void coverRunner.start(works, { replace, label: `${n} cărți selectate` }).then(r => {
        if (r) toast(r.stopped ? 'Căutare oprită.' : `Coperți: ${r.ok} găsite, ${r.fail} negăsite. Le găsești la Filtre → „Alese automat”.`);
      });
      toast('Caut coperțile în fundal… poți continua să folosești aplicația');
    }
    setValue(''); onDone();
  };

  const title = kind === 'shelf' ? `Mută ${n} cărți pe raftul…` : kind === 'destination' ? 'Schimbă destinația' : kind === 'category' ? 'Schimbă categoria' : kind === 'covers' ? `Coperți automate pentru ${n} cărți` : `Ștergi ${myCopies.length} exemplare?`;
  return (
    <Sheet open onClose={onClose} title={title}
      footer={<><button class="btn-ghost" onClick={onClose}>Renunță</button><button class={kind === 'delete' ? 'btn-danger' : 'btn-primary'} onClick={apply} data-testid="batch-apply">{kind === 'delete' ? 'Șterge' : kind === 'covers' ? '✨ Caută coperțile' : 'Aplică'}</button></>}>
      {kind === 'shelf' && (
        <div class="space-y-3">
          <input class="field" list="shelves-dl" value={value} onInput={e => setValue((e.target as HTMLInputElement).value)} placeholder="ex. Raft Living, Birou, Cutia 3" autoFocus />
          <datalist id="shelves-dl">{shelves.map(s => <option key={s} value={s} />)}</datalist>
          <div class="flex flex-wrap gap-2">{shelves.map(s => <button key={s} class="chip-off" onClick={() => setValue(s)}>📍 {s}</button>)}</div>
        </div>
      )}
      {kind === 'destination' && <div class="flex flex-wrap gap-2">{DESTINATIONS.map(d => <button key={d} class={value === d ? 'chip-on' : 'chip-off'} onClick={() => setValue(d)}>{DESTINATION_LABELS[d]}</button>)}</div>}
      {kind === 'category' && <div class="flex flex-wrap gap-2">{CATEGORIES.map(c => <button key={c} class={value === c ? 'chip-on' : 'chip-off'} onClick={() => setValue(c)}>{c}</button>)}</div>}
      {kind === 'covers' && (
        <div class="space-y-3 text-sm" data-testid="batch-covers">
          <p class="text-ink-700">Pentru fiecare carte, aplicația caută coperta (okazii, librării RO, Google Books, Open Library) și o salvează automat pe <b>prima potrivită</b>. Coperțile alese astfel sunt marcate „de verificat”, ca să le poți corecta ușor.</p>
          <label class="flex items-start gap-2 rounded-xl border border-cream-300 bg-white p-3">
            <input type="radio" name="cv" checked={!replace} onChange={() => setReplace(false)} class="mt-1 accent-terra-500" />
            <span><b>Doar cărțile fără copertă</b> ({n - withCover}) <span class="text-ink-500">și cele cu link extern, care se copiază în aplicație</span></span>
          </label>
          <label class="flex items-start gap-2 rounded-xl border border-cream-300 bg-white p-3">
            <input type="radio" name="cv" checked={replace} onChange={() => setReplace(true)} class="mt-1 accent-terra-500" />
            <span><b>Toate cele {n}</b> <span class="text-ink-500">(înlocuiește și coperțile existente cu prima găsită online)</span></span>
          </label>
          <p class="text-xs text-ink-500">≈ 2–4 secunde pe carte, 3 în paralel. Ține aplicația deschisă; progresul apare jos.</p>
        </div>
      )}
      {kind === 'delete' && <p class="text-ink-700">Se șterg doar exemplarele tale. Cărțile rămân în catalog dacă alții au exemplare sau le-au citit.</p>}
      {skipped > 0 && <p class="mt-3 text-xs text-amber-700">{skipped} exemplare aparțin altor membri și nu vor fi modificate.</p>}
    </Sheet>
  );
}

// Rafturile: listă compactă cu căutare + acțiuni pe raft întreg (mută / redenumește / selectează)
function ShelvesSheet({ open, onClose, shelves, current, books, isAdmin, meId, onPick, onSelectShelf }: {
  open: boolean; onClose: () => void; shelves: Array<{ name: string; count: number }>; current: string | null;
  books: BookView[]; isAdmin: boolean; meId?: string; onPick: (s: string | null) => void; onSelectShelf: (s: string) => void;
}) {
  const [q, setQ] = useState('');
  const [action, setAction] = useState<{ shelf: string } | null>(null);
  const [target, setTarget] = useState('');
  if (!open) return null;
  const list = shelves.filter(s => s.name.toLowerCase().includes(q.trim().toLowerCase()));
  const noShelf = books.flatMap(b => b.copies).filter(c => !(c.shelf || '').trim()).length;

  const moveAll = async () => {
    const t = target.trim();
    if (!action || !t) return;
    const ids = books.flatMap(b => b.copies).filter(c => (c.shelf || '') === action.shelf && (isAdmin || c.owner_id === meId)).map(c => c.id);
    await updateCopies(ids, { shelf: t });
    toast(`${ids.length} cărți mutate din „${action.shelf}” în „${t}”`);
    setAction(null); setTarget(''); onPick(t);
  };

  return (
    <Sheet open onClose={() => { setAction(null); onClose(); }} title={action ? `Raftul „${action.shelf}”` : 'Rafturi'}>
      {!action ? (
        <div class="space-y-3">
          <input class="field" placeholder="Caută raftul…" value={q} onInput={e => setQ((e.target as HTMLInputElement).value)} />
          <ul class="divide-y divide-cream-300" data-testid="shelf-list">
            <li><button class={`w-full py-3 text-left font-semibold ${!current ? 'text-terra-600' : ''}`} onClick={() => onPick(null)}>Toate rafturile</button></li>
            {list.map(sh => (
              <li key={sh.name} class="flex items-center gap-2">
                <button class={`flex-1 py-3 text-left ${current === sh.name ? 'font-bold text-terra-600' : ''}`} onClick={() => onPick(sh.name)}>
                  📍 {sh.name} <span class="text-ink-500 text-sm">· {sh.count}</span>
                </button>
                <button class="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => { setAction({ shelf: sh.name }); setTarget(''); }} aria-label={`Acțiuni ${sh.name}`}>Mută / editează</button>
              </li>
            ))}
            {noShelf > 0 && <li class="py-3 text-sm text-ink-500">{noShelf} exemplare fără raft (le poți muta din „Editare în grup”).</li>}
          </ul>
        </div>
      ) : (
        <div class="space-y-4">
          <div>
            <span class="label">Mută toate cărțile de pe acest raft în</span>
            <input class="field" list="all-shelves" placeholder="alt raft sau un nume nou (= redenumire)" value={target} onInput={e => setTarget((e.target as HTMLInputElement).value)} />
            <datalist id="all-shelves">{shelves.filter(s => s.name !== action.shelf).map(s => <option key={s.name} value={s.name} />)}</datalist>
            <div class="mt-2 flex flex-wrap gap-2">{shelves.filter(s => s.name !== action.shelf).slice(0, 8).map(s => <button key={s.name} class="chip-off" onClick={() => setTarget(s.name)}>📍 {s.name}</button>)}</div>
            <button class="btn-primary mt-3 w-full" disabled={!target.trim()} onClick={moveAll} data-testid="move-shelf">Mută toate cărțile</button>
          </div>
          <div class="border-t border-cream-300 pt-4">
            <button class="btn-ghost w-full" onClick={() => onSelectShelf(action.shelf)}>☑ Selectează cărțile de pe raft (pentru alte modificări)</button>
          </div>
          <button class="text-sm text-ink-500 underline" onClick={() => setAction(null)}>← înapoi la rafturi</button>
        </div>
      )}
    </Sheet>
  );
}
